import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import DataSource from 'devextreme/data/data_source';
import CustomStore from 'devextreme/data/custom_store';
import { debounceTime, distinctUntilChanged, firstValueFrom, map } from 'rxjs';
import { imagePlaceholderUrl } from 'sham-components/shared';
import { SiteCategoryNode } from 'sham-components/shared/models';
import { ShamArrowDown01StrokeIcon, ShamSearchIcon } from 'sham-components/icons';
import { MEDIA_BASE_URL } from '@/environments/environment';
import { ContentApi, SiteProductCardDto } from '@/app/services/content-api';
import { WebsiteContext } from '@/app/services/website-context';

export interface SearchCategoryOption {
  id: string;
  name: string;
  level: number;
  path: string[];
  breadcrumb: string;
}

export function flattenSearchCategories(
  categories: SiteCategoryNode[],
  ancestors: string[] = [],
  ancestorNames: string[] = [],
): SearchCategoryOption[] {
  return categories.flatMap((category) => {
    const path = [...ancestors, category.slug];
    const names = [...ancestorNames, category.name];

    return [
      {
        id: category.id,
        name: category.name,
        level: category.level,
        path,
        breadcrumb: names.join(' / '),
      },
      ...flattenSearchCategories(category.children ?? [], path, names),
    ];
  });
}

@Component({
  selector: 'app-marketplace-search',
  imports: [
    DecimalPipe,
    NgTemplateOutlet,
    ReactiveFormsModule,
    TranslateModule,
    ShamArrowDown01StrokeIcon,
    ShamSearchIcon,
  ],
  templateUrl: './marketplace-search.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'contents',
    '(document:keydown)': 'onDocumentKeydown($event)',
  },
})
export class MarketplaceSearch implements OnDestroy {
  private readonly api = inject(ContentApi);
  private readonly ctx = inject(WebsiteContext);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly resultLimit = 6;
  private productRequestId = 0;
  private lastRequestedCriteria: string | null = null;
  private lastLanguage = this.ctx.lang();
  private restoreMobileTriggerFocus = false;
  private productCriteria: { searchTerm?: string; categoryId?: string } = {};

  readonly keywordControl = new FormControl('', { nonNullable: true });
  readonly categoryQueryControl = new FormControl('', { nonNullable: true });
  readonly isSearchOpen = signal(false);
  readonly isCategoryOpen = signal(false);
  readonly selectedCategory = signal<SearchCategoryOption | null>(null);
  readonly categories = signal<SearchCategoryOption[]>([]);
  readonly products = signal<SiteProductCardDto[]>([]);
  readonly totalCount = signal(0);
  readonly isLoading = signal(false);
  readonly hasError = signal(false);
  readonly activeCategoryIndex = signal(0);
  readonly selectedCategoryLabel = computed(
    () => this.selectedCategory()?.name ?? 'All',
  );
  readonly canShowMore = computed(
    () =>
      !!this.selectedCategory() &&
      this.products().length > 0 &&
      this.totalCount() > this.products().length,
  );

  private readonly categoryStore = new CustomStore<SearchCategoryOption, string>({
    key: 'id',
    load: () => {
      const query = this.categoryQueryControl.value.trim().toLocaleLowerCase();
      const options = flattenSearchCategories(this.ctx.categoriesTree());
      return query
        ? options.filter((category) =>
            category.name.toLocaleLowerCase().includes(query),
          )
        : options;
    },
  });

  readonly categoryDataSource = new DataSource<SearchCategoryOption>({
    store: this.categoryStore,
    paginate: false,
  });

  private readonly productStore = new CustomStore<SiteProductCardDto, string>({
    key: 'id',
    load: async () => {
      const requestId = this.productRequestId;
      const response = await firstValueFrom(
        this.api.searchProducts({
          ...this.productCriteria,
          sortBy: 'latest',
          page: 1,
          pageSize: this.resultLimit,
        }),
      );
      if (requestId === this.productRequestId) {
        this.totalCount.set(response.totalCount);
      }
      return response.items;
    },
  });

  readonly productDataSource = new DataSource<SiteProductCardDto>({
    store: this.productStore,
    paginate: false,
  });

  constructor() {
    this.keywordControl.valueChanges
      .pipe(
        map((value) => value.trim()),
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (this.isSearchOpen()) void this.refreshProducts();
      });

    this.categoryQueryControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.refreshCategories());

    effect(() => {
      const language = this.ctx.lang();
      this.ctx.categoriesTree();
      void this.refreshCategories();

      if (language !== this.lastLanguage) {
        this.lastLanguage = language;
        if (untracked(this.isSearchOpen)) void this.refreshProducts(true);
      }
    });
  }

  ngOnDestroy(): void {
    this.categoryDataSource.dispose();
    this.productDataSource.dispose();
  }

  openSearch(focusInput = false): void {
    if (!this.isSearchOpen()) {
      this.isSearchOpen.set(true);
      void this.refreshProducts();
    }

    if (focusInput) {
      setTimeout(() => {
        const inputs = this.host.nativeElement.querySelectorAll<HTMLInputElement>(
          '[data-search-keyword]',
        );
        Array.from(inputs)
          .find((input) => input.offsetParent !== null)
          ?.focus();
      });
    }
  }

  closeSearch(): void {
    const shouldRestoreFocus = this.restoreMobileTriggerFocus;
    this.restoreMobileTriggerFocus = false;
    this.productRequestId++;
    this.lastRequestedCriteria = null;
    this.isSearchOpen.set(false);
    this.isCategoryOpen.set(false);
    this.categoryQueryControl.setValue('', { emitEvent: false });
    this.isLoading.set(false);

    if (shouldRestoreFocus) {
      setTimeout(() =>
        this.host.nativeElement
          .querySelector<HTMLButtonElement>('[data-mobile-search-trigger]')
          ?.focus(),
      );
    }
  }

  toggleCategory(): void {
    this.openSearch();
    this.isCategoryOpen.update((open) => !open);
    this.activeCategoryIndex.set(0);
    if (this.isCategoryOpen()) void this.refreshCategories();
  }

  toggleMobileSearch(): void {
    if (this.isSearchOpen()) {
      this.closeSearch();
    } else {
      this.restoreMobileTriggerFocus = true;
      this.openSearch(true);
    }
  }

  onDocumentKeydown(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.key === 'Escape') {
      this.onEscapeKey();
    } else if (keyboardEvent.key === 'Tab') {
      this.onTabKey(keyboardEvent);
    }
  }

  private onTabKey(keyboardEvent: KeyboardEvent): void {
    if (!this.isSearchOpen()) return;

    const focusable = Array.from(
      this.host.nativeElement.querySelectorAll<HTMLElement>(
        '[data-search-surface] button:not([disabled]), [data-search-surface] input:not([disabled])',
      ),
    ).filter((element) => element.offsetParent !== null);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = this.host.nativeElement.ownerDocument.activeElement;
    if (keyboardEvent.shiftKey && active === first) {
      keyboardEvent.preventDefault();
      last.focus();
    } else if (!keyboardEvent.shiftKey && active === last) {
      keyboardEvent.preventDefault();
      first.focus();
    } else if (!focusable.includes(active as HTMLElement)) {
      keyboardEvent.preventDefault();
      first.focus();
    }
  }

  clearCategory(): void {
    this.selectedCategory.set(null);
    this.categoryQueryControl.setValue('', { emitEvent: false });
    this.isCategoryOpen.set(false);
    this.focusCategoryTrigger();
    void this.refreshCategories();
    void this.refreshProducts();
  }

  selectCategory(category: SearchCategoryOption): void {
    this.selectedCategory.set(category);
    this.categoryQueryControl.setValue('', { emitEvent: false });
    this.isCategoryOpen.set(false);
    this.focusCategoryTrigger();
    void this.refreshCategories();
    void this.refreshProducts();
  }

  onCategoryInputKeydown(event: KeyboardEvent): void {
    const options = this.categories();

    if (event.key === 'Escape') {
      event.stopPropagation();
      this.isCategoryOpen.set(false);
      this.focusCategoryTrigger();
      return;
    }

    if (!options.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeCategoryIndex.update((index) => (index + 1) % options.length);
      this.scrollActiveCategoryIntoView();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeCategoryIndex.update(
        (index) => (index - 1 + options.length) % options.length,
      );
      this.scrollActiveCategoryIntoView();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.selectCategory(options[this.activeCategoryIndex()]);
    }
  }

  onEscapeKey(): void {
    if (!this.isSearchOpen()) return;
    if (this.isCategoryOpen()) {
      this.isCategoryOpen.set(false);
      this.focusCategoryTrigger();
      return;
    }
    this.closeSearch();
  }

  async refreshProducts(force = false): Promise<void> {
    if (!this.isSearchOpen()) return;

    const searchTerm = this.keywordControl.value.trim();
    this.productCriteria = {
      searchTerm: searchTerm || undefined,
      categoryId: this.selectedCategory()?.id,
    };
    const criteriaKey = `${this.productCriteria.searchTerm ?? ''}\u0000${
      this.productCriteria.categoryId ?? ''
    }`;
    if (!force && criteriaKey === this.lastRequestedCriteria) return;
    this.lastRequestedCriteria = criteriaKey;

    const requestId = ++this.productRequestId;
    this.isLoading.set(true);
    this.hasError.set(false);

    try {
      const items = (await this.productDataSource.reload()) as SiteProductCardDto[];
      if (requestId !== this.productRequestId) return;
      this.products.set(items);
    } catch {
      if (requestId !== this.productRequestId) return;
      this.products.set([]);
      this.totalCount.set(0);
      this.hasError.set(true);
    } finally {
      if (requestId === this.productRequestId) this.isLoading.set(false);
    }
  }

  retryProducts(): void {
    void this.refreshProducts(true);
  }

  async openProduct(product: SiteProductCardDto): Promise<void> {
    this.closeSearch();
    await this.router.navigate(['/product', product.slug]);
  }

  async showMore(): Promise<void> {
    const category = this.selectedCategory();
    if (!category) return;

    const searchTerm = this.keywordControl.value.trim() || null;
    this.closeSearch();

    await this.router.navigate(['/categories', ...category.path], {
      queryParams: { q: searchTerm, sort: 'latest' },
    });
  }

  productPrice(product: SiteProductCardDto): number {
    return product.onSale && product.salePrice !== null
      ? product.salePrice
      : product.regularPrice;
  }

  productCurrency(product: SiteProductCardDto): string {
    return product.onSale && product.salePriceCurrency
      ? product.salePriceCurrency
      : product.regularPriceCurrency;
  }

  productImage(product: SiteProductCardDto): string {
    const path = product.mainImageUrl;
    if (!path) return imagePlaceholderUrl(72);

    return path.startsWith('http') ? path : `${MEDIA_BASE_URL}${path}`;
  }

  private async refreshCategories(): Promise<void> {
    const items = (await this.categoryDataSource.reload()) as SearchCategoryOption[];
    this.categories.set(items);
    this.activeCategoryIndex.set(0);
  }

  private focusCategoryTrigger(): void {
    setTimeout(() =>
      Array.from(
        this.host.nativeElement.querySelectorAll<HTMLButtonElement>(
          '[data-category-trigger]',
        ),
      )
        .find((button) => button.offsetParent !== null)
        ?.focus(),
    );
  }

  private scrollActiveCategoryIntoView(): void {
    setTimeout(() => {
      const input = Array.from(
        this.host.nativeElement.querySelectorAll<HTMLInputElement>(
          'input[role="combobox"]',
        ),
      ).find((element) => element.offsetParent !== null);
      const optionId = input?.getAttribute('aria-activedescendant');
      if (optionId) {
        this.host.nativeElement.ownerDocument
          .getElementById(optionId)
          ?.scrollIntoView?.({ block: 'nearest' });
      }
    });
  }
}
